import { Box } from "@chakra-ui/react"
import type { ReactNode } from "react"
import { useIntl } from "react-intl"

import {
  ADMIN_PATHS,
  AUTH_PATHS,
  NETWORK_PATHS,
  PARAMETERS_PATHS,
  PATHS,
  RESOURCES_PATHS,
  SHARING_PATHS,
} from "@/routing/paths"
import { inverseColors } from "@/theme/values"

import useGetUserRoles from "@/hooks/useGetUserRoles"
import useMyAgencyNav from "@/hooks/useMyAgencyNav"
import {
  BooksIcon,
  BuildingIcon,
  ChannelIcon,
  ExternalLinkIcon,
  // QuestionCircleIcon,
  SettingIcon,
  SlidersVAltIcon,
  TachometerFastIcon,
  TreeIcon,
  UserSquareIcon,
} from "../Icons"
import MenuItem, { MenuSection, type MenuSubEntry, type MenuSubGroup } from "./MenuItem"

interface MenuItemsProps {
  collapsed: boolean
  // Called when an item is chosen, so the mobile drawer can close even on the current page.
  onNavigate?: () => void
  // Extra entries of the footer list, after Settings (the collapse toggle of the rail).
  footer?: ReactNode
}

// Entries shared by the desktop rail and the mobile drawer.
export default function MenuItems({ collapsed, onNavigate, footer }: MenuItemsProps) {
  const intl = useIntl()
  const { hasRoleGuru } = useGetUserRoles()
  const { displayMyAgency, myAgencyLabelId } = useMyAgencyNav()

  const entry = (labelId: string, path: string, options?: Partial<MenuSubEntry>) => ({
    label: intl.formatMessage({ id: labelId }),
    path,
    ...options,
  })

  // Sub-entries of the sections, formerly the column of each page (SubNavLayout).
  const networkGroups: MenuSubGroup[] = [
    {
      entries: [
        ...(displayMyAgency
          ? [entry(myAgencyLabelId, `${PATHS.NETWORK}${NETWORK_PATHS.MY_COLLABORATORS}`)]
          : []),
        // The agencies entry opens the international directory (ADR 0012, revision of 2026-10-13).
        entry("network.barnes_agencies", `${PATHS.NETWORK}${NETWORK_PATHS.DIRECTORY}`),
        entry("network.barnes_collaborators", `${PATHS.NETWORK}${NETWORK_PATHS.COLLABORATORS}`),
      ],
    },
  ]
  const sharingGroups: MenuSubGroup[] = [
    {
      entries: [
        entry("sharing.interbu", `${PATHS.SHARING}${SHARING_PATHS.INTER_BU}`),
        entry("sharing.people", `${PATHS.SHARING}${SHARING_PATHS.PEOPLE}`),
        entry("sharing.project", `${PATHS.SHARING}${SHARING_PATHS.PROJECT}`),
      ],
    },
  ]
  const resourcesGroups: MenuSubGroup[] = [
    {
      entries: [
        entry("resources.documents", `${PATHS.RESOURCES}${RESOURCES_PATHS.DOCUMENTS}`),
        entry("resources.mail_sign", `${PATHS.RESOURCES}${RESOURCES_PATHS.MAIL_SIGN}`),
        entry("resources.help", `${PATHS.RESOURCES}${RESOURCES_PATHS.HELP}`),
      ],
    },
  ]
  const admin = (labelId: string, path: ADMIN_PATHS) => entry(labelId, `${PATHS.ADMIN}${path}`)
  const adminGroups: MenuSubGroup[] = [
    {
      label: intl.formatMessage({ id: "menu.admin.main_entities" }),
      entries: [
        admin("network.companies", ADMIN_PATHS.COMPANIES),
        admin("network.agencies", ADMIN_PATHS.AGENCIES),
        admin("network.clusters", ADMIN_PATHS.CLUSTERS),
        admin("network.collaborators", ADMIN_PATHS.COLLABORATORS),
      ],
    },
    {
      label: intl.formatMessage({ id: "menu.admin.rpp_data" }),
      entries: [
        admin("network.people", ADMIN_PATHS.PEOPLE),
        admin("network.projects", ADMIN_PATHS.PROJECTS),
        admin("network.projects_offer", ADMIN_PATHS.PROJECTS_OFFER),
        admin("network.projects_search", ADMIN_PATHS.PROJECTS_REQUEST),
        admin("network.properties", ADMIN_PATHS.PROPERTIES),
      ],
    },
    {
      label: intl.formatMessage({ id: "menu.admin.mybarnes_users" }),
      entries: [admin("network.users", ADMIN_PATHS.USERS)],
    },
    {
      label: intl.formatMessage({ id: "menu.admin.parameters" }),
      entries: [
        admin("network.ref_labels", ADMIN_PATHS.REF_LABELS),
        admin("network.segments", ADMIN_PATHS.SEGMENTS),
        admin("network.synchro", ADMIN_PATHS.SYNCHRO),
        admin("network.dashboard", ADMIN_PATHS.DASHBOARD),
      ],
    },
  ]
  const parametersGroups: MenuSubGroup[] = [
    {
      entries: [
        entry("parameters.profile.title", `${PATHS.PARAMETERS}${PARAMETERS_PATHS.PROFIL}`, {
          exact: true,
        }),
        entry("parameters.logout", `${PATHS.AUTH}${AUTH_PATHS.LOGOUT}`),
      ],
    },
  ]

  return (
    <>
      <Box flex="1 1 auto" minH={0} overflowY="auto" pb="4">
        <Box as="ul" listStyleType="none" m={0} p={0}>
          <MenuItem
            collapsed={collapsed}
            onClick={onNavigate}
            icon={TachometerFastIcon}
            path={PATHS.DASHBOARD}
            exact
            label={intl.formatMessage({ id: "menu.dashboard" })}
          />
          <MenuItem
            collapsed={collapsed}
            onClick={onNavigate}
            icon={UserSquareIcon}
            path={PATHS.CUSTOMERS}
            label={intl.formatMessage({ id: "menu.clients" })}
          />
          <MenuItem
            collapsed={collapsed}
            onClick={onNavigate}
            icon={BuildingIcon}
            path={PATHS.PROPERTIES}
            label={intl.formatMessage({ id: "menu.properties" })}
          />
          <MenuSection
            collapsed={collapsed}
            onClick={onNavigate}
            icon={TreeIcon}
            path={PATHS.NETWORK}
            groups={networkGroups}
            label={intl.formatMessage({ id: "menu.network" })}
          />
          <MenuSection
            collapsed={collapsed}
            onClick={onNavigate}
            icon={ChannelIcon}
            path={PATHS.SHARING}
            groups={sharingGroups}
            label={intl.formatMessage({ id: "menu.sharing" })}
          />
          <MenuSection
            collapsed={collapsed}
            onClick={onNavigate}
            icon={BooksIcon}
            path={PATHS.RESOURCES}
            groups={resourcesGroups}
            label={intl.formatMessage({ id: "menu.resources" })}
          />
          <MenuItem
            collapsed={collapsed}
            onClick={onNavigate}
            icon={ExternalLinkIcon}
            path={PATHS.DIGITAL_TOOLS}
            label={intl.formatMessage({ id: "menu.digital_tools" })}
          />
          {/*
          <MenuItem
            collapsed={collapsed}
            icon={QuestionCircleIcon}
            path={PATHS.HELP}
            label={intl.formatMessage({ id: "menu.help" })}
          />
          */}
          {hasRoleGuru() && (
            <>
              <MenuSection
                collapsed={collapsed}
                onClick={onNavigate}
                icon={SlidersVAltIcon}
                path={PATHS.ADMIN}
                groups={adminGroups}
                label={intl.formatMessage({ id: "menu.admin" })}
              />
              {/**
              <MenuItem
                collapsed={collapsed}
                icon={SlidersVAltIcon}
                path={PATHS.TADMIN}
                label={intl.formatMessage({ id: "menu.tadmin" })}
              />
              */}
            </>
          )}
        </Box>
      </Box>
      <Box
        as="ul"
        listStyleType="none"
        m={0}
        py="2"
        px={0}
        flex="none"
        borderTop="1px solid"
        borderTopColor={inverseColors.line}
      >
        <MenuSection
          collapsed={collapsed}
          onClick={onNavigate}
          icon={SettingIcon}
          path={PATHS.PARAMETERS}
          groups={parametersGroups}
          label={intl.formatMessage({ id: "menu.parameters" })}
        />
        {footer}
      </Box>
    </>
  )
}
